import React from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonBackButton,
  IonSpinner,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useAppConfig } from '@/modules/config';
import SnowballMenu from '@/modules/user/SnowballMenu';
import PageRefresher from '@/components/PageRefresher';
import { useTopicForm } from '@/modules/topic/useTopicForm';
import { useTopicController } from '@/modules/topic/useTopicController';
import { useTopicState } from '@/modules/topic/TopicStateContext';
import { pageBackground } from '@/modules/topic/read/styles';
import DeleteTopicSheet from '@/modules/topic/read/DeleteTopicSheet';
import NotFoundCard from '@/modules/topic/read/NotFoundCard';
import PrivateInvitePlaceholder from '@/modules/topic/read/PrivateInvitePlaceholder';
import ShareDock from '@/modules/topic/read/ShareDock';
import { useTopicReadInterstitials } from '@/modules/topic/read/useTopicReadInterstitials';
import { topicComponentRegistry } from './registry';

interface TopicReadPageProps {
  topicId: string;
}

/**
 * The stable component-driven read page shell. It stays mounted for the whole read
 * lifecycle — the pre-load / by-invitation-only masked phase and the loaded phase
 * alike — owning the `IonPage` frame, the header, the share dock, the delete sheet,
 * and the phone-verification / private-invite interstitials. Only the inner body
 * swaps (spinner → placeholder → the loaded topic's per-type body), so unlocking a
 * topic never remounts the interstitial host and strands its backdrop (#524).
 *
 * Once the topic loads (and its type is known) the shell dispatches the scrollable
 * body to the type's own `<type>/ReadOnlyView`, which inlines the hero and read
 * sections and may diverge per type, and the welcome sheet to the type's own
 * `<type>/WelcomeInterstitial`.
 */
const TopicReadPage: React.FC<TopicReadPageProps> = ({ topicId }) => {
  const history = useHistory();
  const { features } = useAppConfig();
  const form = useTopicForm();
  const {
    topic,
    registration,
    loading,
    deleting,
    showDeleteSheet,
    setShowDeleteSheet,
    fetchTopicInfo,
    handleEnterUpdate,
    handleShareTopic,
    handleShareUpdates,
    handleDeleteTopic,
  } = useTopicController({
    initialMode: 'view',
    topicId,
    resetCreateForm: form.resetCreateForm,
    resetFormFromTopic: form.resetFormFromTopic,
  });
  const { state: topicState } = useTopicState();
  const topicMutations = topicState.mutationsById?.[topicId] || [];
  // The loaded type's own welcome sheet. Until the topic (and thus its type)
  // resolves the sheet stays closed, so the shell renders the standard one and
  // swaps to the type's before it can open.
  const WelcomeComponent = topic
    ? topicComponentRegistry[topic.type]?.WelcomeInterstitial
    : undefined;
  const {
    invitePreviewData,
    privateInviteResolved,
    goToProfileWizardForInvite,
    interstitials,
  } = useTopicReadInterstitials({
    topicId,
    topic,
    loading,
    initialMode: 'view',
    onTopicBecameReadable: fetchTopicInfo,
    WelcomeComponent,
  });

  const notFound =
    !loading && !topic && !invitePreviewData && privateInviteResolved;

  const body = () => {
    if (loading || (!topic && !invitePreviewData && !privateInviteResolved)) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <IonSpinner name="crescent" />
        </div>
      );
    }
    if (!topic && invitePreviewData) {
      return (
        <PrivateInvitePlaceholder
          preview={invitePreviewData}
          onLinkPhone={goToProfileWizardForInvite}
        />
      );
    }
    if (!topic) {
      return <NotFoundCard />;
    }
    const ReadOnlyView = topicComponentRegistry[topic.type]?.ReadOnlyView;
    if (!ReadOnlyView) {
      return <NotFoundCard />;
    }
    return (
      <ReadOnlyView
        topic={topic}
        topicId={topicId}
        registration={registration}
        deleting={deleting}
        registrationEnabled={!!features.topicRegistrationEnabled}
        topicMutations={topicMutations}
        onEdit={handleEnterUpdate}
        onRequestDelete={() => setShowDeleteSheet(true)}
        onOpenPath={path => history.push(path)}
        onGuardSuccess={fetchTopicInfo}
      />
    );
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          {!loading && (
            <IonButtons slot="start">
              <IonBackButton defaultHref="/topics" />
            </IonButtons>
          )}
          {notFound ? (
            <IonTitle>Topic Not Found</IonTitle>
          ) : (
            <IonTitle className="ion-hide-accessible">Topic Details</IonTitle>
          )}
          <IonButtons slot="end">
            <SnowballMenu />
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent style={pageBackground}>
        <PageRefresher />
        {body()}
        {topic && (
          <DeleteTopicSheet
            topicTitle={topic.title}
            isOpen={showDeleteSheet}
            onDismiss={() => setShowDeleteSheet(false)}
            onDelete={handleDeleteTopic}
          />
        )}
      </IonContent>
      {topic && (
        <ShareDock
          topic={topic}
          topicId={topicId}
          features={features}
          topicMutations={topicMutations}
          onShareTopic={handleShareTopic}
          onShareUpdates={handleShareUpdates}
          onGuardSuccess={fetchTopicInfo}
        />
      )}
      {interstitials}
    </IonPage>
  );
};

export default TopicReadPage;
